import type { AffixDef, AffixId, DieSides, MasterDie } from '../core/types';
import * as coreAffixes from '../core/affixes';
import { AFFIXES, AFFIX_BY_ID } from '../data/affixes';
import { MATERIALS, isMaterialId, type MaterialId } from '../data/materials';
import { dieMaterialOf, isEnriched } from './abyssApi';
import { sidesRank, sortLevelOf } from './diceTabs';
import { readSessionPref, writeSessionPref } from './sessionPref';

/**
 * Tri et filtres des dés (v0.55.8, A119) — la **logique pure** du panneau
 * « Tri et filtres » : un seul état de vue pour tout le jeu (Inventaire,
 * Recycleur à l'unité, cibles de la Forge, fenêtre « Équiper l'emplacement N »),
 * mémorisé le temps de la session. Aucun DOM ici ; le panneau est
 * `screens/accalmie/diceViewPanel.ts`.
 */

export type DiceSortKey = 'level' | 'enriched' | 'power' | 'sides' | 'affix';
export type EnrichedFilter = 'all' | 'yes' | 'no';

export interface DiceView {
  /** `level` par défaut (A103 : niveau, faces, score). */
  sort: DiceSortKey;
  enriched: EnrichedFilter;
  material: MaterialId | 'all';
  sides: DieSides | 'all';
  /** Affixe cherché (A122-13) ; `'all'` : pas de filtre par affixe. */
  affix: AffixId | 'all';
  /** Seuil de la somme effective (fraction : 0,3 = « ≥ 30 % ») ; 0 par défaut, ignoré hors affixe à plage. */
  affixMin: number;
}

export const DEFAULT_DICE_VIEW: DiceView = Object.freeze({
  sort: 'level',
  enriched: 'all',
  material: 'all',
  sides: 'all',
  affix: 'all',
  affixMin: 0,
}) as DiceView;

export const DICE_SORT_KEYS: readonly DiceSortKey[] = ['level', 'enriched', 'power', 'sides', 'affix'];

/** Clés de tri proposées : « Affixe » seulement quand un affixe est choisi (A122-13). */
export function sortKeysFor(v: Pick<DiceView, 'affix'>): DiceSortKey[] {
  return DICE_SORT_KEYS.filter((k) => k !== 'affix' || v.affix !== 'all');
}

/** Libellé de chaque clé de tri (liste déroulante). */
export const DICE_SORT_LABEL: Record<DiceSortKey, string> = {
  level: 'Niveau',
  enriched: 'Enrichi d’abord',
  power: 'Puissance',
  sides: 'Faces',
  affix: 'Affixe',
};

/** Libellé court d'une clé de tri (résumé : « Tri : Enrichi »). */
const SORT_SHORT: Record<DiceSortKey, string> = {
  level: 'Niveau',
  enriched: 'Enrichi',
  power: 'Puissance',
  sides: 'Faces',
  affix: 'Affixe',
};

export const ENRICHED_LABEL: Record<EnrichedFilter, string> = {
  all: 'Tous',
  yes: 'Enrichis seulement',
  no: 'Non enrichis',
};

const KNOWN_SIDES: readonly number[] = [4, 6, 12, 20, 100];

const VIEW_KEY = 'diceordie.dice.view';
const VIEW_OPEN_KEY = 'diceordie.dice.viewOpen';

// ───────────────────────────────────────────────────────── Mémoire de session

/** Lecture tolérante : une valeur illisible (ou un champ inconnu) retombe sur le défaut. */
export function readDiceView(): DiceView {
  const raw = readSessionPref(VIEW_KEY);
  if (!raw) return { ...DEFAULT_DICE_VIEW };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_DICE_VIEW };
  }
  if (typeof parsed !== 'object' || parsed === null) return { ...DEFAULT_DICE_VIEW };
  const p = parsed as Record<string, unknown>;
  // A122-13 : champs absents (préférences d'avant) → défaut ; un affixe inconnu retombe sur « Tous ».
  const affix: DiceView['affix'] = typeof p.affix === 'string' && isChoosableAffix(p.affix) ? (p.affix as AffixId) : 'all';
  const affixMin =
    affix !== 'all' && affixHasThreshold(affix) && typeof p.affixMin === 'number' && Number.isFinite(p.affixMin)
      ? Math.min(2, Math.max(0, Math.round(p.affixMin * 100) / 100))
      : 0;
  const sort = DICE_SORT_KEYS.includes(p.sort as DiceSortKey) ? (p.sort as DiceSortKey) : DEFAULT_DICE_VIEW.sort;
  return {
    affix,
    affixMin,
    sort: sort === 'affix' && affix === 'all' ? DEFAULT_DICE_VIEW.sort : sort,
    enriched: p.enriched === 'yes' || p.enriched === 'no' ? p.enriched : 'all',
    material: isMaterialId(p.material) ? p.material : 'all',
    sides: typeof p.sides === 'number' && KNOWN_SIDES.includes(p.sides) ? (p.sides as DieSides) : 'all',
  };
}

export function writeDiceView(v: DiceView): void {
  writeSessionPref(VIEW_KEY, JSON.stringify(v));
}

/** Le panneau est replié par défaut ; son ouverture est mémorisée pour la session. */
export function readDiceViewOpen(): boolean {
  return readSessionPref(VIEW_OPEN_KEY) === '1';
}

export function writeDiceViewOpen(open: boolean): void {
  writeSessionPref(VIEW_OPEN_KEY, open ? '1' : '0');
}

// ───────────────────────────────────────────────────────────────── Lectures

export function isDefaultView(v: DiceView): boolean {
  return v.sort === 'level' && !hasActiveFilter(v);
}

/** Un filtre (et pas seulement le tri) est-il actif ? */
export function hasActiveFilter(v: DiceView): boolean {
  return v.enriched !== 'all' || v.material !== 'all' || v.sides !== 'all' || v.affix !== 'all';
}

// ───────────────────────────────────────────────────────── Filtre par affixe

/** Affixes proposés au filtre : les tirables (`AFFIXES` sans les nommés), dans l'ordre des données. */
export function choosableAffixes(): AffixDef[] {
  return AFFIXES.filter((a) => !a.named);
}

function isChoosableAffix(id: string): boolean {
  const def = AFFIX_BY_ID[id as AffixId];
  return !!def && !def.named;
}

/** Valeur fixe d'après le cœur (`isFixedAffix`), à défaut une plage réduite à un point. */
function isFixed(def: AffixDef): boolean {
  const fn = (coreAffixes as unknown as { isFixedAffix?: (d: AffixDef) => boolean }).isFixedAffix;
  return typeof fn === 'function' ? fn(def) : def.min === def.max;
}

/**
 * Le champ « ≥ X % » existe-t-il pour cet affixe ? Seulement un affixe **à plage**
 * en pourcentage : les affixes à valeur fixe (Avantageux, Réplicant) et *Pipé*
 * (à plage mais de valeur fixe) se filtrent par « porte l'affixe ».
 */
export function affixHasThreshold(id: AffixId): boolean {
  const def = AFFIX_BY_ID[id];
  return !!def && def.unit === 'percent' && !isFixed(def) && id !== 'loaded';
}

/** Somme effective de l'affixe sur le dé (Enrichi compris), 0 s'il ne le porte pas. */
export function effectiveAffixSum(die: MasterDie, id: AffixId): number {
  return coreAffixes.affixValue(die, id);
}

function carriesAffix(die: MasterDie, id: AffixId): boolean {
  return die.affixes.some((a) => a.id === id);
}

/** Le dé passe-t-il le filtre par affixe de la vue ? */
function matchesAffix(die: MasterDie, v: DiceView): boolean {
  if (v.affix === 'all') return true;
  if (!carriesAffix(die, v.affix)) return false;
  if (!affixHasThreshold(v.affix) || v.affixMin <= 0) return true;
  return effectiveAffixSum(die, v.affix) + 1e-9 >= v.affixMin;
}

/** Nom d'un Matériau (`MATERIALS`), l'identifiant à défaut. */
export function materialName(id: MaterialId): string {
  return MATERIALS.find((m) => m.id === id)?.name ?? id;
}

/** « Tri : Enrichi · Enrichis seulement · Boisé · d20 », ou « Tri : Niveau » pour la vue par défaut. */
export function viewSummary(v: DiceView): string {
  const parts = [`Tri : ${SORT_SHORT[v.sort]}`];
  if (v.enriched !== 'all') parts.push(ENRICHED_LABEL[v.enriched]);
  if (v.material !== 'all') parts.push(materialName(v.material));
  if (v.sides !== 'all') parts.push(`d${v.sides}`);
  if (v.affix !== 'all') {
    const name = AFFIX_BY_ID[v.affix]?.name ?? v.affix;
    parts.push(affixHasThreshold(v.affix) && v.affixMin > 0 ? `${name} ≥ ${Math.round(v.affixMin * 100)} %` : name);
  }
  return parts.join(' · ');
}

/** Matériaux portés par au moins un dé, dans l'ordre de `MATERIALS`. */
export function presentMaterials(dice: readonly MasterDie[]): MaterialId[] {
  const seen = new Set(dice.map((d) => dieMaterialOf(d)));
  return MATERIALS.map((m) => m.id).filter((id) => seen.has(id));
}

/** Faces présentes, du d100 au d4 (même ordre que les volets). */
export function presentSides(dice: readonly MasterDie[]): DieSides[] {
  return [...new Set(dice.map((d) => d.sides))].sort((a, b) => sidesRank(a) - sidesRank(b));
}

// ───────────────────────────────────────────────────────────── Filtre et tri

/** Filtres en ET ; la vue par défaut ne retire rien. */
export function filterDice(dice: readonly MasterDie[], v: DiceView): MasterDie[] {
  return dice.filter((d) => {
    if (v.enriched === 'yes' && !isEnriched(d)) return false;
    if (v.enriched === 'no' && isEnriched(d)) return false;
    if (v.material !== 'all' && dieMaterialOf(d) !== v.material) return false;
    if (v.sides !== 'all' && d.sides !== v.sides) return false;
    if (!matchesAffix(d, v)) return false;
    return true;
  });
}

/**
 * Tri par la clé de la vue ; à égalité, l'ordre d'arrivée départage.
 *  - `level` : niveau ↓ (un dé de campagne vaut 0), rang de faces, score ↓ (= `sortWithinPanel`) ;
 *  - `enriched` : Enrichis d'abord, puis comme `level` ;
 *  - `power` : score ↓, puis niveau ↓, rang de faces ;
 *  - `sides` : rang de faces (d100, d20, d12, d6, d4), puis niveau ↓, score ↓ ;
 *  - `affix` (A122-13) : somme effective de l'affixe choisi ↓ (Enrichi compris), puis niveau ↓, faces, score ↓.
 */
export function sortDice(dice: readonly MasterDie[], v: DiceView, score: (d: MasterDie) => number): MasterDie[] {
  const rows = dice.map((die, index) => ({
    die,
    index,
    level: sortLevelOf(die),
    rank: sidesRank(die.sides),
    score: score(die),
    rich: isEnriched(die) ? 1 : 0,
    affix: v.affix === 'all' ? 0 : effectiveAffixSum(die, v.affix),
  }));
  type Row = (typeof rows)[number];
  const byKey: Record<DiceSortKey, (a: Row, b: Row) => number> = {
    level: (a, b) => b.level - a.level || a.rank - b.rank || b.score - a.score,
    enriched: (a, b) => b.rich - a.rich || b.level - a.level || a.rank - b.rank || b.score - a.score,
    power: (a, b) => b.score - a.score || b.level - a.level || a.rank - b.rank,
    sides: (a, b) => a.rank - b.rank || b.level - a.level || b.score - a.score,
    affix: (a, b) => b.affix - a.affix || b.level - a.level || a.rank - b.rank || b.score - a.score,
  };
  const cmp = byKey[v.sort];
  return rows.sort((a, b) => cmp(a, b) || a.index - b.index).map((r) => r.die);
}
